import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { APIError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { hasPerm } from '@/lib/perms'
import { PERM } from '@/types/api'

export function LoginPage() {
  const { profile, login } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // PX-73: landing target is permission-based, not role-based. Users
  // with `users.read` land on the Dashboard; everyone else gets
  // /photoshoots (the canonical surface for non-admin privileged roles).
  if (profile) {
    const target = hasPerm(profile, PERM.users_read) ? '/' : '/photoshoots'
    return <Navigate to={target} replace />
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await login(email, password)
    } catch (err) {
      if (err instanceof APIError) setError(err.message)
      else setError('Login failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex h-full w-full items-center justify-center bg-ink-50 p-6">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-2xl border border-ink-100 bg-white p-6 shadow-sm"
      >
        <div className="mb-6 flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-brand-500" />
          <div>
            <h1 className="text-lg font-semibold tracking-tight">Pinxie Admin</h1>
            <p className="text-xs text-ink-400">Internal — any privileged role allowed</p>
          </div>
        </div>

        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium text-ink-500">Email</span>
          <input
            type="email"
            required
            autoFocus
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </label>

        <label className="mb-4 block">
          <span className="mb-1 block text-xs font-medium text-ink-500">Password</span>
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </label>

        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-md bg-brand-500 px-3 py-2 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  )
}
